/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import {
  Animation,
  Colors,
  useClickOutside,
  ZapierLogo,
  Typography,
} from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { useState } from 'react';
import {
  skipLinkHref as skipLinkHrefDefault,
  UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
  UNIVERSAL_TOPBAR_HEIGHT,
} from '../../../constants';
import { useUniversalNavContext } from '../../../hooks';
import SkipLink from '../../SkipLink';
import UniversalTopbarLink from '../UniversalTopbarLink';

import AccountActions from '../AccountActions';
import SearchForm from '../SearchForm';
import SearchToggler from './SearchToggler';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';

import type { Props as SearchFormProps } from '../SearchForm';
import type { TopbarProps } from '../types';
import { emitFullStoryEvent } from '../../../utils/fullStoryEvents';
import PlanSnapshotPopover from '../../PlanSnapshotPopover/PlanSnapshotPopover';

const Styles = labelStyles('UniversalTopbar', {
  root: [
    {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: `${Colors.neutral100}`,
      position: 'relative', // Needed for submenu dropdown
      borderBottom: `1px solid ${Colors.neutral300}`,
    },
  ],
  header: ({ hasSidebar }: { hasSidebar: boolean }) => [
    css({
      width: '100%',
      maxWidth: '1200px',
      height: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
      padding: '0 22px 0 10px',
      display: 'grid',
      gridTemplateColumns: 'max-content auto max-content',
      alignItems: 'stretch',
      gap: '5px',
      backgroundColor: Colors.neutral100,

      '@media (min-width: 450px)': {
        height: UNIVERSAL_TOPBAR_HEIGHT,
        padding: '0 20px',
        gap: '0',
      },
    }),
    hasSidebar &&
      withModifier(
        'has-sidebar',
        css({
          maxWidth: 'none',
          // Whenever there's a sidebar, and especially a sidebar toggle,
          // the toggle button itself takes up enough space to warrant
          // collapsing the padding on the left side.
          paddingLeft: '10px',
        })
      ),
  ],
  group: css({
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    justifySelf: 'flex-end',
    gap: '5px',

    '@media (min-width: 450px)': {
      gap: '17px',
    },
  }),
  mainNavigationContent: [
    {
      display: 'flex',
      alignItems: 'stretch',
      marginLeft: 25,
    },
  ],
  searchForm: (
    props: TopbarProps,
    { isSearching }: { isSearching: boolean }
  ) => [
    {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: Animation.transitionValue,
      opacity: 0,
      pointerEvents: 'none',
      transform: 'scaleX(0)',
    },
    isSearching &&
      withModifier('searching', {
        opacity: 1,
        pointerEvents: 'auto',
        transform: 'scaleX(1)',
      }),
    props.isSearchFullWidth &&
      withModifier('fullWidth', {
        backgroundColor: `${Colors.neutral100}`,
        padding: '0 10px',
      }),
  ],
  linkButton: css({
    '& span': {
      ...Typography.paragraph3,
      fontWeight: 'normal',
      color: Colors.neutral800,
    },
  }),
  searchToggler: ({ isSearching }: { isSearching: boolean }) => [
    {
      marginRight: -7,
      transition: Animation.transitionValue,
    },
    isSearching &&
      withModifier('hidden', {
        opacity: 0.2,
        pointerEvents: 'none',
      }),
  ],
  logoLinksWrapper: {
    // This section has an element with position: absolute,
    // relative positioning here will define the bounds
    position: 'relative',
  },
  skipLink: {
    position: 'absolute',
    // Positions the SkipLink just below the logo
    top: '100%',
    whiteSpace: 'nowrap',
  },
});

/**
 * @private
 * Renders universal dynamic navigation top bar with Zapier logo, search,
 * links and account actions.
 */
const InAppTopbar = ({
  isSearchFullWidth,
  logoAriaLabel = 'Go to the Zapier Homepage',
  logoHref = '/',
  onLogoClick = () => {},
  selectedHref = '',
  shouldRenderLogo = true,
  size = 'medium',
  skipLinkHref = skipLinkHrefDefault,
  renderAccountMenu = (props: TopbarProps) => (
    <AccountActions
      selectedHref={props.selectedHref}
      shouldRenderSignIn={props.size !== 'small'}
      shouldRenderSignUp={true}
      size={props.size}
    />
  ),
  renderNotifications = () => null,
  renderSearchForm = (props: SearchFormProps) => (
    <SearchForm
      isSearching={props.isSearching}
      onClose={props.onClose}
      searchInputId={props.searchInputId}
    />
  ),
  ...otherProps
}: TopbarProps) => {
  const {
    currentUrl,
    getFullyQualifiedURL,
    shouldRenderCallToAction,
    topNavData,
    shouldRenderUpgradeCTA,
  } = useUniversalNavContext();

  const props = {
    isSearchFullWidth,
    logoAriaLabel,
    logoHref,
    onLogoClick,
    renderAccountMenu,
    renderNotifications,
    renderSearchForm,
    selectedHref,
    shouldRenderLogo,
    size,
    skipLinkHref,
    ...otherProps,
  } satisfies TopbarProps;

  const sidebar = props.renderSidebar && props.renderSidebar(props);
  const logoSize = {
    width: 105,
    height: 28,
  };

  const searchInputId = 'universal-nav-app-search';
  // Track whether the user is currently searching.
  const [isSearching, setIsSearching] = useState(false);
  const onCloseSearch = () => {
    setIsSearching(false);
  };
  const searchForm = props.renderSearchForm({
    isSearching,
    searchInputId,
    onClose: onCloseSearch,
  });
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: (event: MouseEvent | TouchEvent) => {
      if (props.onClickOutside) props.onClickOutside(event);
    },
  });
  const callToActionUrl =
    getFullyQualifiedURL(
      `${topNavData.callToAction?.href}?demo_source=cs_nav_header_${currentUrl}`
    ) || '';

  const upgradeCTAUrl = getFullyQualifiedURL(topNavData.upgradeCTA?.href) || '';

  return (
    <div css={Styles.root} ref={(node) => setClickOutsideNode(node)} data-zds>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        css={Styles.header({ hasSidebar: !!sidebar })}
        data-testid="universalTopbar"
        onClick={props.onClick}
        data-zds
      >
        <div css={Styles.group} data-zds>
          {sidebar}
          {props.shouldRenderLogo && (
            <header css={Styles.logoLinksWrapper}>
              <a
                aria-label={props.logoAriaLabel}
                href={getFullyQualifiedURL(props.logoHref)}
                onClick={props.onLogoClick}
                data-zds
              >
                <ZapierLogo {...logoSize} />
              </a>

              <div css={Styles.skipLink} data-zds>
                <SkipLink href={props.skipLinkHref} />
              </div>
            </header>
          )}
          {size !== 'small' && topNavData.planSnapshot && (
            <PlanSnapshotPopover
              {...topNavData.planSnapshot}
              upgradeUrl={getFullyQualifiedURL('/app/planbuilder/plans') || ''}
            />
          )}
        </div>
        <div css={Styles.mainNavigationContent} data-zds>
          {
            <div
              aria-label="Search apps"
              css={Styles.searchForm(props, { isSearching })}
              onBlur={() => {
                setIsSearching(false);
              }}
              onFocus={() => {
                setIsSearching(true);
                emitUserInteractionEvent({
                  interaction_name: searchInputId,
                  event_action: 'in_view',
                  option_selected: 'Universal Nav Search opened',
                });
              }}
              role="form"
              data-zds
            >
              {searchForm}
            </div>
          }
        </div>
        <div
          aria-label="Manage your account and search apps"
          css={Styles.group}
          role="form"
          data-zds
        >
          {!!searchForm && (
            <div
              css={Styles.searchToggler({ isSearching })}
              data-testid="searchToggler"
              data-zds
            >
              <SearchToggler
                id={searchInputId}
                isSearching={false}
                size={props.size}
              />
            </div>
          )}

          {topNavData.linksInApp?.map((link) => (
            <UniversalTopbarLink
              navigationItem={{
                variant: 'link',
                ...topNavData.callToAction,
                href: link.href,
                text: size === 'medium' ? link.text : '',
              }}
              position="actions-menu"
              isButton={false}
              icon={link.icon}
              ariaLabel={link.text}
              key={link.text}
            >
              {size === 'medium' ? link.text : ''}
            </UniversalTopbarLink>
          ))}

          {topNavData.callToAction &&
            props.size !== 'small' &&
            shouldRenderCallToAction && (
              <UniversalTopbarLink
                navigationItem={{
                  variant: 'link',
                  ...topNavData.callToAction,
                  href: callToActionUrl,
                }}
                position="actions-menu"
                isButton={true}
              >
                {topNavData.callToAction.text}
              </UniversalTopbarLink>
            )}

          {topNavData.upgradeCTA &&
            props.size !== 'small' &&
            shouldRenderUpgradeCTA && (
              <UniversalTopbarLink
                navigationItem={{
                  variant: 'link',
                  ...topNavData.upgradeCTA,
                  href: upgradeCTAUrl,
                  text: topNavData.upgradeCTA.text,
                  color: topNavData.upgradeCTA.color,
                }}
                position="actions-menu"
                isButton={true}
                ariaLabel={topNavData.upgradeCTA.text}
                key={topNavData.upgradeCTA.text}
                onClick={() => {
                  const eventProperties = {
                    interaction_name:
                      'universal_topbar_upgrade_cta_button_clicked',
                    event_action: 'click',
                    option_selected: 'n/a',
                    experiment_flag: true,
                    experiment_name: 'revenue-growth-universal-nav-upgrade-cta',
                    interaction_raw_text: topNavData.upgradeCTA?.text ?? '',
                  };

                  emitUserInteractionEvent(eventProperties);
                  emitFullStoryEvent(eventProperties);
                }}
              >
                {topNavData.upgradeCTA?.text}
              </UniversalTopbarLink>
            )}

          {props.renderAccountMenu(props)}
        </div>
      </div>
    </div>
  );
};

export default InAppTopbar;
